import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { competitionAPI } from '../../api/endpoints';
import CompetitionCard from './CompetitionCard';
import { FaPlus, FaSearch, FaFilter, FaTimes } from 'react-icons/fa';
import LoadingSpinner from '../common/LoadingSpinner';
import './CompetitionList.css';

const CompetitionList = () => {
  const { isAuthenticated, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [competitions, setCompetitions] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, pages: 0, current: 1 });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    visibility: '',
    sort: 'recent',
  });
  const [showFilters, setShowFilters] = useState(false);

  const fetchCompetitions = async () => {
    setLoading(true);
    try {
      const params = {
        per_page: 20,
        page: pagination.current,
        search: filters.search || undefined,
        status: filters.status || undefined,
      };
      const response = await competitionAPI.getList(params);
      setCompetitions(response.data.data || []);
      setPagination({
        total: response.data.total || 0,
        pages: response.data.pages || 0,
        current: pagination.current,
      });
    } catch (error) {
      console.error('Failed to fetch competitions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompetitions();
  }, [pagination.current, filters]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchCompetitions();
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const clearFilters = () => {
    setFilters({ search: '', status: '', visibility: '', sort: 'recent' });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, current: page }));
  };

  if (loading && pagination.current === 1) {
    return (
      <div className="competition-list-loading">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="competition-list-container">
      <div className="competition-list-header">
        <div>
          <h2>Competitions</h2>
          <p className="subtitle">Join competitions and test your prediction skills</p>
        </div>
        {isAuthenticated && user?.punter_plus && (
          <Link to="/competitions/create" className="btn btn-primary">
            <FaPlus /> Create Competition
          </Link>
        )}
      </div>

      {/* Search and Filters */}
      <div className="competition-search">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search competitions..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="search-input"
            />
          </div>
          <button 
            type="button" 
            className="filter-toggle"
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter /> Filters
          </button>
          <button type="submit" className="btn btn-primary">Search</button>
        </form>

        {showFilters && (
          <div className="filters-panel">
            <div className="filter-group">
              <label>Status</label>
              <select 
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Status</option>
                <option value="open">Open</option>
                <option value="upcoming">Upcoming</option>
                <option value="ended">Ended</option>
                <option value="full">Full</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Visibility</label>
              <select 
                value={filters.visibility}
                onChange={(e) => handleFilterChange('visibility', e.target.value)}
              >
                <option value="">All</option>
                <option value="public">Public</option>
                <option value="private">Private</option>
              </select>
            </div>
            <button onClick={clearFilters} className="btn btn-secondary btn-sm">
              <FaTimes /> Clear
            </button>
          </div>
        )}
      </div>

      {/* Results Count */}
      <div className="results-info">
        <span>{pagination.total} competitions found</span>
      </div>

      {/* Competition Grid */}
      {competitions.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🏆</div>
          <h3>No competitions found</h3>
          <p>Try adjusting your search or filters</p>
          {isAuthenticated && user?.punter_plus && (
            <Link to="/competitions/create" className="btn btn-primary">
              Create the first competition
            </Link>
          )}
        </div>
      ) : (
        <div className="competition-grid">
          {competitions.map((comp) => (
            <CompetitionCard key={comp.id} competition={comp} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => handlePageChange(pagination.current - 1)}
            disabled={pagination.current === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          <div className="pagination-pages">
            {Array.from({ length: Math.min(pagination.pages, 5) }, (_, i) => {
              let pageNum;
              if (pagination.pages <= 5) {
                pageNum = i + 1;
              } else if (pagination.current <= 3) {
                pageNum = i + 1;
              } else if (pagination.current >= pagination.pages - 2) {
                pageNum = pagination.pages - 4 + i;
              } else {
                pageNum = pagination.current - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`pagination-page ${pagination.current === pageNum ? 'active' : ''}`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          <button 
            onClick={() => handlePageChange(pagination.current + 1)}
            disabled={pagination.current === pagination.pages}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default CompetitionList;